import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip";
import SpeedDial from "@mui/material/SpeedDial";
import SpeedDialIcon from "@mui/material/SpeedDialIcon";
import SpeedDialAction from "@mui/material/SpeedDialAction";
import StorageIcon from "@mui/icons-material/Storage";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import AddToQueueIcon from "@mui/icons-material/AddToQueue";
import ClickAwayListener from "@mui/base/ClickAwayListener";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import useDictionary from "@ui/hooks/useDictionary";
import DateFilterComponent from "@ui/components/Date-filter/Date-filter.component";
import SelectComponent from "@ui/components/Select/Select.component";
import SwitchComponent from "../Device-settings/Switch.component";
import AddGroupContainer from "../Add-group/Add-group.container";
import { IProps } from "./IProps";
import { variants } from "@ui/components/Select/variants.enum";
import DeviceMenuHistory from "./Device-menu.comp";
import { SelectChangeEvent } from "@mui/material";
import { useStore } from "@ui/hooks/useStore";

const Component: React.FC<IProps> = ({
    devices,
    fromDateFilterCurrent,
    toDateFilterCurrent,
    dateFilter,
    lang,
    userType,
    addGroupVisible,
    speedDialHover,
    addDialogOpen,
    selectedDevice,
    trackMode,
    timezone,
    groups,
    onFromDateChange,
    onToDateChange,
    onDateFilterChange,
    onDeviceSelect,
    onAddGroupOpen,
    onAddGroupClose,
    onAddDeviceInDbOpen,
    onAddDeviceOpen,
    onAddDialog,
    onAddDiologClose,
    onSpeedDialHoverStart,
    onSpeedDialHoverStop,
    onSwitchTrackMode,
    onUpdateClick,
    onDeviceInfoOpen,
    onDeviceSettingsOpen,
    onDeviceDescriptionOpen,
    onDeviceBtnHoverStart,
    onDeviceBtnHoverStop,
}: IProps) => {
    const getWord = useDictionary();
    const { map } = useStore();
    const popperClass = speedDialHover
        ? "hidden"
        : "groups__menu-tooltip-popper";

    /* Group selection */
    const [selectedGroup, setSelectedGroup] = useState<string>("");

    useEffect(() => {
        setSelectedGroup(groups.groupsList[0]?.id || "");
    }, [groups.groupsList]);
    
    const groupsList = useMemo(() => {
        return groups.groupsList.map((group) => ({
            value: group.id,
            label: group.name,
        }));
    }, [groups.groupsList]);

    const handleGroupChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        setSelectedGroup(value);
    };

    /* - */

    const devicesList = useMemo(() => {
        //console.dir(devices.map(({ name, groupId }) => ({ name, group: selectedGroup }))) // O(n^2)
        return devices
            .filter((device) => device.groupId === selectedGroup)
            .map(({ imei: value, name: label }) => ({
                value,
                label,
            }));
    }, [devices, selectedGroup]);

    useEffect(() => {        
        if (devicesList[0] && devicesList[0].value) {   
            if (!map.selectedDevice) {
                map.setSelectedDevice(devicesList[0].value);
            }
        }
    }, [devicesList]);

    const dateFiltersList = [
        "last24Hours",
        "last3Days",
        "lastWeek",
        "customDate",
    ];
    const getDateFilter = () => {
        let dateFilterComponent: React.ReactElement;
        if (dateFilter === "customDate") {
            dateFilterComponent = (
                <DateFilterComponent
                    lang={lang}
                    timezone={timezone}
                    fromDateFilter={fromDateFilterCurrent}
                    toDateFilter={toDateFilterCurrent}
                    onFromDateChange={onFromDateChange}
                    onToDateChange={onToDateChange}
                />
            );
        } else dateFilterComponent = <></>;
        return (
            <>
                <SelectComponent
                    name="date-filter"
                    lang={lang}
                    value={dateFilter}
                    values={dateFiltersList}
                    variant={variants.sidebar}
                    onChange={onDateFilterChange}
                />
                {dateFilterComponent}
            </>
        );
    };
    const dateFilters = getDateFilter();
    const getDeviceCreateBtn = () => {
        if (userType === "superuser") {
            return (
                <SpeedDialAction
                    icon={<StorageIcon />}
                    tooltipTitle={getWord("addDeviceInDb")}
                    FabProps={{
                        tabIndex: 0,
                    }}
                    onClick={onAddDeviceInDbOpen}
                    onTouchStartCapture={onSpeedDialHoverStart}
                    onTouchEndCapture={onSpeedDialHoverStop}
                    onMouseOver={onSpeedDialHoverStart}
                    onMouseOut={onSpeedDialHoverStop}
                    onMouseEnter={onSpeedDialHoverStart}
                    onFocus={onSpeedDialHoverStart}
                    onMouseLeave={onSpeedDialHoverStop}
                    onBlur={onSpeedDialHoverStop}
                />
            );
        }
    };
    const getAddDeviceBtn = () => {
        if (userType === "superuser" || userType === "regular") {
            return (
                <SpeedDialAction
                    icon={<AddToQueueIcon />}
                    tooltipTitle={getWord("addDevice")}
                    FabProps={{
                        tabIndex: 0,
                    }}
                    onClick={onAddDeviceOpen}
                    onTouchStartCapture={onSpeedDialHoverStart}
                    onTouchEndCapture={onSpeedDialHoverStop}
                    onMouseOver={onSpeedDialHoverStart}
                    onMouseOut={onSpeedDialHoverStop}
                    onMouseEnter={onSpeedDialHoverStart}
                    onFocus={onSpeedDialHoverStart}
                    onMouseLeave={onSpeedDialHoverStop}
                    onBlur={onSpeedDialHoverStop}
                />
            );
        }
    };
    const createDeviceBtn = getDeviceCreateBtn();
    const addDeviceBtn = getAddDeviceBtn();

    return (
        <>
            <Box className="history-menu" component="section">
                <FormControl className="history-menu__form" component="form">
                    <SelectComponent
                        label="group"
                        name="group"
                        lang={lang}
                        value={selectedGroup}
                        values={groupsList}
                        variant={variants.sidebar}
                        onChange={handleGroupChange}
                    />
                    <Box
                        sx={{
                            marginRight: "-32px",
                            width: "calc(100% + 32px)",
                            flexDirection: "row",
                        }}
                        className="history-menu__switch-wrap"
                    >
                        <SelectComponent
                            label="device"
                            name="device"
                            lang={lang}
                            value={selectedDevice}
                            values={devicesList}
                            variant={variants.sidebar}
                            onChange={onDeviceSelect}
                        />
                        <DeviceMenuHistory
                            id={selectedDevice}
                            onDeviceInfoOpen={onDeviceInfoOpen} //
                            onDeviceDescriptionOpen={onDeviceDescriptionOpen} //
                            onDeviceSettingsOpen={onDeviceSettingsOpen}
                            onDeviceBtnHoverStart={onDeviceBtnHoverStart}
                            onDeviceBtnHoverStop={onDeviceBtnHoverStop}
                        />
                    </Box>
                    {dateFilters}
                    <Box className="history-menu__switch-wrap">
                        <SwitchComponent
                            alarmState={trackMode}
                            name={"trackMode"}
                            onAlarmStateChange={onSwitchTrackMode}
                        />
                        <Button
                            className="history-menu__update-btn"
                            variant="contained"
                            onClick={onUpdateClick}
                        >
                            {getWord("update")}
                        </Button>
                    </Box>
                </FormControl>
                <Box className="groups__menu">
                    <ClickAwayListener onClickAway={onAddDiologClose}>
                        <Tooltip
                            classes={{
                                popper: popperClass,
                                tooltip: "groups__menu-tooltip",
                            }}
                            title={getWord("addObject")}
                            placement="top"
                        >
                            <SpeedDial
                                classes={{
                                    root: "groups__menu-btn-wrap",
                                    fab: "groups__menu-btn",
                                    actions: "groups__menu-actions",
                                }}
                                direction="left"
                                ariaLabel="Add button"
                                icon={<SpeedDialIcon />}
                                onClick={onAddDialog}
                                open={addDialogOpen}
                            >
                                {createDeviceBtn}
                                {addDeviceBtn}
                                <SpeedDialAction
                                    icon={<DevicesOtherIcon />}
                                    tooltipTitle={getWord("addGroup")}
                                    FabProps={{
                                        tabIndex: 0,
                                    }}
                                    onClick={onAddGroupOpen}
                                    onTouchStartCapture={onSpeedDialHoverStart}
                                    onTouchEndCapture={onSpeedDialHoverStop}
                                    onMouseOver={onSpeedDialHoverStart}
                                    onMouseOut={onSpeedDialHoverStop}
                                    onMouseEnter={onSpeedDialHoverStart}
                                    onFocus={onSpeedDialHoverStart}
                                    onMouseLeave={onSpeedDialHoverStop}
                                    onBlur={onSpeedDialHoverStop}
                                />
                            </SpeedDial>
                        </Tooltip>
                    </ClickAwayListener>
                </Box>
            </Box>
            <Modal open={addGroupVisible} onClose={onAddGroupClose}>
                <AddGroupContainer onClose={onAddGroupClose} />
            </Modal>
        </>
    );
};

const HistoryMenuComponent = React.memo(Component);

export default HistoryMenuComponent;
